import { InputNumber as AntInputNumber } from 'antd';
import type { InputNumberProps } from 'antd/es/input-number';
import { useId, useMemo, useState } from 'react';
import cn from 'classnames';
import inputStyles from '../input.module.scss';
import controlStyles from './input-number.module.scss';

type AppSize = NonNullable<InputNumberProps['size']> | 'lg' | 'md' | 'sm';

export type AppInputNumberProps = Omit<InputNumberProps<number>, 'size'> & {
    label?: string;
    requiredMark?: boolean;
    footnoteMark?: boolean;
    size?: AppSize;
    underline?: boolean;
};

export function AppInputNumber(props: AppInputNumberProps) {
    const {
        label,
        requiredMark,
        footnoteMark,
        className,
        value,
        defaultValue,
        onFocus,
        onBlur,
        size,
        underline = true,
        ...restProps
    } = props;

    const {
        rootClassName,
        className: inputClassName,
        controls,
        disabled,
        id: inputId,
        required,
        ...inputProps
    } = restProps as AppInputNumberProps & {
        rootClassName?: string;
        className?: string;
        controls?: InputNumberProps<number>['controls'];
        disabled?: boolean;
        id?: string;
        required?: boolean;
    };

    const id = useId();
    const [focused, setFocused] = useState(false);

    const hasValue = useMemo(() => {
        const v = value ?? defaultValue;
        if (v === 0) return true;
        return v !== undefined && v !== null && String(v).trim().length > 0;
    }, [value, defaultValue]);

    const occupied = focused || hasValue;

    const antdSize: InputNumberProps<number>['size'] =
        size === 'lg'
            ? 'large'
            : size === 'md'
            ? 'middle'
            : size === 'sm'
            ? 'small'
            : size;

    const resolvedId = inputId ?? id;
    const controlsValue = controls ?? false;
    const isDisabled = disabled ?? false;

    return (
        <div
            className={cn(inputStyles.root, className, {
                [inputStyles.isFocused]: focused,
                [inputStyles.noUnderline]: !underline,
                [inputStyles.isDisabled]: isDisabled,
            })}
        >
            <AntInputNumber<number>
                id={resolvedId}
                {...inputProps}
                value={value}
                defaultValue={defaultValue}
                size={antdSize}
                bordered={false}
                controls={controlsValue} // стрілочки сховані за замовчанням
                disabled={isDisabled}
                required={required}
                className={inputClassName}
                rootClassName={cn(
                    inputStyles.control,
                    controlStyles.control,
                    rootClassName
                )}
                onFocus={e => {
                    setFocused(true);
                    onFocus?.(e as any);
                }}
                onBlur={e => {
                    setFocused(false);
                    onBlur?.(e as any);
                }}
            />

            {label && (
                <label
                    htmlFor={resolvedId}
                    className={cn(
                        inputStyles.label,
                        occupied
                            ? inputStyles.asLabel
                            : inputStyles.asPlaceholder
                    )}
                >
                    {label}
                    {(required || requiredMark) && (
                        <span className={inputStyles.requiredMark}>*</span>
                    )}
                    {footnoteMark && (
                        <span className={inputStyles.footnoteMark}>*</span>
                    )}
                </label>
            )}
        </div>
    );
}
